import { useEffect, useState } from "react";
import axios from "axios";
import {
  Target, Eye, Sparkles, MessageCircle,
  BookOpen, ShieldCheck,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * DynamicIntelligentBriefingCard — Founder Operational Experience V1
 * ------------------------------------------------------------------
 * Calm briefing card shown in Work Mode's pre-lesson (ARRIVED)
 * stage. Instructor guidance ONLY. Not an AI interview, not a data
 * collection form, not tick boxes. Glanceable, readable card the
 * instructor uses naturally with the pupil.
 *
 * Doctrine:
 *   - Briefing helps the instructor prepare the pupil.
 *   - Debrief helps the AI learn from the instructor.
 *   - Never present a recommendation without explaining what it
 *     looks like in practice.
 */
// Slice C · Visual correction (2026-08-17). One continuous
// working surface. No outer decorative container, no nested
// section cards, no status pills. Hierarchy comes from
// typography and thin dividers only. `BriefingSection` is
// declared at module scope so React does not re-mount the
// subtree on every parent render.
function BriefingSection({ testid, icon: Icon, iconClass, label, first, children }) {
  return (
    <div data-testid={testid} className={first ? "" : "pt-5 mt-5 border-t border-slate-800/70"}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-3.5 h-3.5 ${iconClass}`} />
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
          {label}
        </p>
      </div>
      {children}
    </div>
  );
}

export default function DynamicIntelligentBriefingCard({ studentId, lessonId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!studentId) { setLoading(false); return; }
    setLoading(true);
    setError(false);
    axios
      .get(`${API}/api/work-mode/dynamic-briefing`, {
        params: { student_id: studentId, lesson_id: lessonId || undefined },
        withCredentials: true,
      })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [studentId, lessonId]);

  if (loading) {
    return (
      <div data-testid="dynamic-briefing-loading" className="py-4 animate-pulse">
        <div className="h-4 bg-slate-800/60 rounded w-1/2 mb-3" />
        <div className="h-3 bg-slate-800/60 rounded w-4/5" />
      </div>
    );
  }
  if (error || !data) {
    // Calm-baseline silence — do not shout a failure at the instructor.
    return null;
  }

  const resp = data.objective?.responsibility_level || "not_confirmed";
  const respLabel = ({
    shared_low: "shared — you lead",
    shared_medium: "shared — 50/50",
    learner_led: "learner-led — you observe",
    shared: "shared",
    not_confirmed: "Confirm support with the pupil",
  })[resp] || "Confirm support with the pupil";

  // Slice C · Visual correction (2026-08-17). Hierarchy from
  // typography + subtle dividers. See module-scope `BriefingSection`.

  return (
    <section data-testid="dynamic-briefing-card" className="py-2">
      {/* Header — typography only, no icon square. */}
      <header data-testid="briefing-header" className="mb-5">
        <p className="text-white text-lg font-extrabold leading-tight">
          Today with {data.pupil?.name || "your pupil"}
        </p>
        <p className="text-[13px] text-emerald-300 mt-1 font-semibold">
          {data.objective?.level ? `Challenge: Level ${data.objective.level}` : "Today's lesson"}
        </p>
      </header>

      {/* Objective */}
      <BriefingSection
        testid="briefing-objective"
        icon={Target}
        iconClass="text-emerald-400"
        label="Today's objective"
        first
      >
        <p className="text-white font-bold text-[15px] leading-snug">
          {data.objective?.title}
        </p>
        {data.objective?.why && data.objective.why !== data.objective?.title && (
          <p className="text-slate-400 text-sm mt-1 leading-relaxed">{data.objective.why}</p>
        )}
      </BriefingSection>

      <details className="pt-3 mt-3 border-t border-slate-800/70">
        <summary className="text-sm font-semibold text-sky-300 cursor-pointer">More briefing</summary>
        <div className="pt-1">

      {/* Responsibility — pill replaced with plain italic text on its own line. */}
      <BriefingSection
        testid="briefing-responsibility"
        icon={ShieldCheck}
        iconClass="text-amber-400"
        label="Responsibility today"
      >
        <p data-testid="briefing-responsibility-level" className="text-slate-400 text-[13px] italic mb-2">
          {respLabel}
        </p>
        <p className="text-slate-300 text-sm leading-relaxed">
          <span className="text-slate-500">What it looks like: </span>
          {data.objective?.what_it_looks_like}
        </p>
      </BriefingSection>

      {/* Previous lesson reminders */}
      {Array.isArray(data.previous_reminders) && data.previous_reminders.length > 0 && (
        <BriefingSection
          testid="briefing-previous-reminders"
          icon={BookOpen}
          iconClass="text-sky-400"
          label="Previous lesson reminders"
        >
          <ul className="space-y-1.5">
            {data.previous_reminders.map((r, i) => (
              <li key={i} className="text-slate-300 text-sm leading-relaxed flex gap-2">
                <span className="text-slate-600 mt-0.5">·</span>
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </BriefingSection>
      )}

      {/* Suggested questions to ask */}
      {Array.isArray(data.suggested_questions) && data.suggested_questions.length > 0 && (
        <BriefingSection
          testid="briefing-suggested-questions"
          icon={MessageCircle}
          iconClass="text-fuchsia-400"
          label="Questions to ask"
        >
          <ul className="space-y-1.5">
            {data.suggested_questions.map((q, i) => (
              <li key={i} className="text-slate-300 text-sm leading-relaxed flex gap-2">
                <span className="text-slate-600 mt-0.5">·</span>
                <span>{q}</span>
              </li>
            ))}
          </ul>
        </BriefingSection>
      )}

      {/* Watch for */}
      {Array.isArray(data.watch_for) && data.watch_for.length > 0 && (
        <BriefingSection
          testid="briefing-watch-for"
          icon={Eye}
          iconClass="text-rose-400"
          label="Watch for"
        >
          <ul className="space-y-1.5">
            {data.watch_for.map((w, i) => (
              <li key={i} className="text-slate-300 text-sm leading-relaxed flex gap-2">
                <span className="text-slate-600 mt-0.5">·</span>
                <span>{w}</span>
              </li>
            ))}
          </ul>
        </BriefingSection>
      )}

      {/* Success looks like — no green panel; same section rhythm. */}
      {Array.isArray(data.success_looks_like) && data.success_looks_like.length > 0 && (
        <BriefingSection
          testid="briefing-success"
          icon={Sparkles}
          iconClass="text-emerald-400"
          label="Success today looks like"
        >
          <ul className="space-y-1.5">
            {data.success_looks_like.map((s, i) => (
              <li key={i} className="text-slate-300 text-sm leading-relaxed flex gap-2">
                <span className="text-slate-600 mt-0.5">·</span>
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </BriefingSection>
      )}

      {data.intelligence && (
        <BriefingSection
          testid="briefing-intelligence-panel"
          icon={BookOpen}
          iconClass="text-sky-400"
          label="Understanding & progress (awareness only)"
        >
          {data.intelligence.cross_engine_reason && (
            <p
              data-testid="briefing-intelligence-reason"
              className="text-slate-300 text-sm leading-relaxed"
            >
              {data.intelligence.cross_engine_reason}
            </p>
          )}
          {Array.isArray(data.intelligence.candidate_observation_focus)
            && data.intelligence.candidate_observation_focus.length > 0 && (
              <p
                data-testid="briefing-intelligence-focus"
                className="text-[12px] text-slate-400 mt-2"
              >
                <span className="text-slate-500">Candidate focus: </span>
                <span className="text-slate-200">
                  {data.intelligence.candidate_observation_focus
                    .map((d) => d.replace(/_/g, " "))
                    .join(", ")}
                </span>
              </p>
            )}
          <p
            data-testid="briefing-intelligence-guidance"
            className="text-[11px] text-slate-500 mt-2 italic"
          >
            {data.intelligence.guidance
              || "Awareness only. Not a diagnosis. Your judgement remains primary."}
          </p>
        </BriefingSection>
      )}

      <p className="text-[11px] text-slate-500 italic pt-6 mt-6 border-t border-slate-800/70">
        Briefing is guidance — not a form. Refer to it as needed.
      </p>
        </div>
      </details>
    </section>
  );
}
